import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    Children,
    Fragment,
    isValidElement,
    type ReactElement,
    type ReactNode,
    useEffect,
    useId,
    useState
} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useServerTableContext} from "./ServerTableContext";
import {
    ServerTableToolbarContext,
    ServerTableToolbarItemContext
} from "./ServerTableToolbarContext";
import type {ServerTableToolbarPanel} from "./ServerTableToolbarContext";
import {useServerTableToolbarHost} from "./ServerTableToolbarHost";
import {getServerTableToolbarItemDefinition} from "./ServerTableToolbarItem";
import type {
    ServerTableToolbarAlign,
    ServerTableToolbarItemDefinition
} from "./ServerTableToolbarItem";
import {
    SERVER_TABLE_CONTROL_SIZE,
    serverTableControlIconSx,
    serverTableControlSx
} from "./constants";
import type {ServerTableToolbarProps} from "./types";

// Control size plus the gap the header keeps between it and its neighbour.
const MORE_BUTTON_RESERVE = SERVER_TABLE_CONTROL_SIZE + 8;

interface FlattenedToolbarChild {
    node: ReactNode
    sourceIndex: number
}

interface ManagedToolbarItem extends FlattenedToolbarChild {
    node: ReactElement
    definition: ServerTableToolbarItemDefinition
    align: ServerTableToolbarAlign
}

const flattenToolbarChildren = (
    children: ReactNode,
    flattened: FlattenedToolbarChild[] = []
) => {
    Children.toArray(children).forEach(node => {
        if (isValidElement<{children?: ReactNode}>(node)
            && node.type === Fragment) {
            flattenToolbarChildren(node.props.children, flattened);
            return;
        }

        flattened.push({node, sourceIndex: flattened.length});
    });

    return flattened;
};

// A control may declare its side once (registered components) or per call site
// (`align` on a generic action), so page-owned scope pickers can sit left without
// giving up the collapse-into-More behaviour registration buys them.
const resolveAlign = (
    node: ReactElement,
    definition: ServerTableToolbarItemDefinition
): ServerTableToolbarAlign => {
    const {align} = node.props as {align?: ServerTableToolbarAlign};
    return align ?? definition.align ?? 'end';
};

/** Lays out explicitly composed controls and coordinates their responsive panels. */
export const ServerTableToolbar = ({
    children,
    hoistOnCompact,
    paddingX = 0
}: ServerTableToolbarProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const {searchQuery, isFullScreen} = useServerTableContext();
    const toolbarHost = useServerTableToolbarHost();
    const [activePanel, setActivePanel] = useState<{
        type: ServerTableToolbarPanel
        anchor: HTMLElement
    } | null>(null);
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(Boolean(searchQuery.trim()));

    // Registered controls take a side; unregistered children stay pinned left.
    const managedItems: ManagedToolbarItem[] = [];
    const pinnedChildren: FlattenedToolbarChild[] = [];
    flattenToolbarChildren(children).forEach(child => {
        if (isValidElement(child.node)) {
            const definition = getServerTableToolbarItemDefinition(child.node.type);
            if (definition) {
                managedItems.push({
                    ...child,
                    node: child.node,
                    definition,
                    align: resolveAlign(child.node, definition)
                });
                return;
            }
        }

        pinnedChildren.push(child);
    });

    // Hoisting hands the managed controls to a page header that has room for them,
    // so a screen whose table is the whole page does not spend a second band on a
    // row of icons. Search then opens as a panel: an inline field cannot take over
    // a header it shares with a title and a back button.
    // Fullscreen covers the page header, taking the hoisted controls - and the way
    // back out - with it, so the row comes home for as long as it lasts.
    const isHoisted = Boolean(hoistOnCompact)
        && isCompact
        && !isFullScreen
        && !!toolbarHost.actions;
    const searchItem = managedItems.find(item => item.definition.role === 'search');
    const isSearchExpanded = !!searchItem && isSearchOpen;
    const isOverlaySearch = isHoisted && isSearchExpanded && !!toolbarHost.searchOverlay;
    const isOverflowPresentation = isCompact || (isSearchExpanded && !isHoisted);
    const startItems = isOverflowPresentation
        ? []
        : managedItems.filter(item => item.align === 'start');
    const endItems = isOverflowPresentation
        // While search owns the header the button behind it would only collect focus.
        ? (isOverlaySearch ? [] : managedItems.filter(item => item.definition.role === 'search'))
        : managedItems.filter(item => item.align === 'end');
    const menuItems = isOverflowPresentation
        ? managedItems.filter(item => item.definition.role !== 'search')
        : [];
    const hasLeftContent = pinnedChildren.length > 0 || startItems.length > 0;
    const hasManagedContent = endItems.length > 0 || menuItems.length > 0;
    const showToolbarRow = hasLeftContent || (!isHoisted && hasManagedContent);

    // Panels anchor to a control that a presentation change unmounts.
    useEffect(() => {
        setActivePanel(null);
        setMoreAnchor(null);
    }, [isCompact, isHoisted]);

    // The hoisted row leaves nothing behind to expand into, and fullscreen brings it
    // back mid-search; either way the field must not survive the move.
    useEffect(() => {
        setIsSearchOpen(false);
    }, [isHoisted]);

    // A search restored by a pasted URL or browser navigation must remain visible.
    useEffect(() => {
        if (searchQuery.trim()) setIsSearchOpen(true);
    }, [searchQuery]);

    const closeMenu = () => setMoreAnchor(null);
    const buttonId = useId();
    const menuId = useId();

    const managedControls = hasManagedContent && (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={[
                {
                    flex: '0 0 auto',
                    minWidth: 0,
                    ml: isHoisted ? 0 : 'auto',
                    columnGap: {xs: 0.25, sm: 0.5, md: 0.75}
                },
                isSearchExpanded && !isHoisted && {
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    ml: 0,
                    bgcolor: 'background.paper'
                }
            ]}
        >
            {endItems.map(item => (
                <ServerTableToolbarItemContext.Provider
                    key={item.sourceIndex}
                    value={{
                        presentation: 'toolbar',
                        menuAnchor: null,
                        closeMenu
                    }}
                >
                    {item.node}
                </ServerTableToolbarItemContext.Provider>
            ))}

            {menuItems.length > 0 && (
                <Tooltip title={t('More')}>
                    <IconButton
                        id={buttonId}
                        size="small"
                        color="primary"
                        data-dashboard-control=""
                        data-active={!!moreAnchor || !!activePanel || undefined}
                        aria-label={t('More')}
                        aria-haspopup="menu"
                        aria-expanded={!!moreAnchor}
                        aria-controls={moreAnchor ? menuId : undefined}
                        onClick={event => {
                            setActivePanel(null);
                            setMoreAnchor(event.currentTarget);
                        }}
                        sx={{...serverTableControlSx, flexShrink: 0}}
                    >
                        <MoreVertIcon sx={serverTableControlIconSx}/>
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );

    return (
        <ServerTableToolbarContext.Provider value={{
            activePanel,
            openPanel: (type, anchor) => setActivePanel({type, anchor}),
            closePanel: () => setActivePanel(null),
            searchSurface: isHoisted ? 'overlay' : 'inline',
            isSearchOpen: isSearchExpanded,
            openSearch: () => setIsSearchOpen(true),
            closeSearch: () => setIsSearchOpen(false)
        }}>
            {showToolbarRow && (
                <Box
                    component="header"
                    sx={{
                        position: 'relative',
                        isolation: 'isolate',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'nowrap',
                        width: '100%',
                        minWidth: 0,
                        minHeight: {xs: 40, md: 44},
                        py: {xs: 0.5, sm: 0},
                        px: isFullScreen
                            ? Math.max(paddingX, 2)
                            : {xs: 0, md: paddingX},
                        columnGap: {xs: 0.25, sm: 0.5, md: 0.75},
                        mb: 1
                    }}
                >
                    {hasLeftContent && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                columnGap: {xs: 0.25, sm: 0.5, md: 0.75}
                            }}
                        >
                            {pinnedChildren.map(child => (
                                <Fragment key={`pinned-${child.sourceIndex}`}>
                                    {child.node}
                                </Fragment>
                            ))}

                            {startItems.map(item => (
                                <ServerTableToolbarItemContext.Provider
                                    key={item.sourceIndex}
                                    value={{
                                        presentation: 'toolbar',
                                        menuAnchor: null,
                                        closeMenu
                                    }}
                                >
                                    {item.node}
                                </ServerTableToolbarItemContext.Provider>
                            ))}
                        </Stack>
                    )}

                    {!isHoisted && managedControls}
                </Box>
            )}

            {isHoisted && managedControls && toolbarHost.actions
                && createPortal(managedControls, toolbarHost.actions)}

            {isOverlaySearch && searchItem && toolbarHost.searchOverlay && createPortal(
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        flex: 1,
                        minWidth: 0,
                        // Leaves the More button uncovered; everything else in the
                        // header belongs to search until the user closes it.
                        mr: menuItems.length > 0 ? `${MORE_BUTTON_RESERVE}px` : 0,
                        bgcolor: 'background.paper',
                        pointerEvents: 'auto'
                    }}
                >
                    <ServerTableToolbarItemContext.Provider
                        value={{presentation: 'overlay', menuAnchor: null, closeMenu}}
                    >
                        {searchItem.node}
                    </ServerTableToolbarItemContext.Provider>
                </Box>,
                toolbarHost.searchOverlay
            )}

            {menuItems.length > 0 && (
                <Menu
                    id={menuId}
                    keepMounted
                    anchorEl={moreAnchor}
                    open={!!moreAnchor}
                    onClose={closeMenu}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    MenuListProps={{
                        'aria-labelledby': buttonId
                    }}
                >
                    {menuItems.map(item => (
                        <ServerTableToolbarItemContext.Provider
                            key={item.sourceIndex}
                            value={{
                                presentation: 'menu',
                                menuAnchor: moreAnchor,
                                closeMenu
                            }}
                        >
                            {item.node}
                        </ServerTableToolbarItemContext.Provider>
                    ))}
                </Menu>
            )}
        </ServerTableToolbarContext.Provider>
    );
};
